import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AlertTriangle } from 'react-feather';
import { Badge, Button, Card, Col, Form, Row } from 'react-bootstrap';
import Chart from 'react-apexcharts';
import { toast } from 'react-toastify';
import { getPromptDetailAPI, publishPromptAPI, runPromptTestAPI } from '../../../Services/mockApi';
import PageHeader from '../../../Components/shared/PageHeader';
import LoadingSkeleton from '../../../Components/shared/LoadingSkeleton';
import EmptyState from '../../../Components/shared/EmptyState';
import { platformUrl } from '../../../utils/mhNav';
import styles from './PromptEditor.module.scss';

export default function PromptEditor() {
  const { id } = useParams();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [systemPrompt, setSystemPrompt] = useState('');
  const [developerRules, setDeveloperRules] = useState('');
  const [examples, setExamples] = useState([]);
  const [sampleInput, setSampleInput] = useState('Book 30 minutes with the finance team next Tuesday.');
  const [outputJson, setOutputJson] = useState('');
  const [confidence, setConfidence] = useState(0);
  const [validation, setValidation] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getPromptDetailAPI(id);
      setDetail(res.data);
      setSystemPrompt(res.data.systemPrompt || '');
      setDeveloperRules(res.data.developerRules || '');
      setExamples(res.data.examples || []);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const schemaOk = validation?.schemaOk ?? false;
  const canPublish = schemaOk && validation?.fieldsOk && validation?.confidenceOk;

  const radialOptions = useMemo(
    () => ({
      chart: { sparkline: { enabled: true } },
      plotOptions: {
        radialBar: {
          hollow: { size: '62%' },
          dataLabels: {
            name: { show: true, fontSize: '12px', offsetY: 10 },
            value: { fontSize: '22px', offsetY: -10, formatter: (v) => `${(v / 100).toFixed(2)}` },
          },
        },
      },
      labels: ['Confidence'],
    }),
    []
  );

  const radialSeries = useMemo(() => [Math.round(confidence * 100)], [confidence]);

  const runTest = async () => {
    try {
      const res = await runPromptTestAPI(id, sampleInput);
      setOutputJson(res.data.outputJson);
      setConfidence(typeof res.data.confidence === 'number' ? res.data.confidence : 0.91);
      setValidation(res.data.validation);
      toast.success('Test completed');
    } catch (e) {
      toast.error(e.message);
    }
  };

  const addExample = () => {
    setExamples((ex) => [
      ...ex,
      { id: `ex-${Date.now()}`, input: '', outputJson: '{}' },
    ]);
  };

  if (loading && !detail) {
    return (
      <div>
        <PageHeader title="Prompt editor" breadcrumbs={[{ label: 'Platform', path: platformUrl('dashboard') }, { label: 'AI policies', path: platformUrl('ai-policies') }, { label: id }]} />
        <LoadingSkeleton rows={5} cols={2} />
      </div>
    );
  }

  if (error && !detail) {
    return (
      <div>
        <PageHeader title="Prompt editor" breadcrumbs={[{ label: 'Platform', path: platformUrl('dashboard') }, { label: 'AI policies', path: platformUrl('ai-policies') }, { label: id }]} />
        <EmptyState icon={AlertTriangle} title="Could not load" description={error.message} actionLabel="Retry" onAction={load} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={detail?.name || 'Policy'}
        breadcrumbs={[
          { label: 'Platform', path: platformUrl('dashboard') },
          { label: 'AI policies', path: platformUrl('ai-policies') },
          { label: 'Prompt' },
        ]}
      />

      <Row className="g-3">
        <Col lg={7}>
          <Card className="mb-3">
            <Card.Body>
              <div className="mb-2 d-flex flex-wrap gap-1">
                <Badge bg="dark">{detail?.module}</Badge>
                <Badge bg="secondary">{detail?.scopeLabel}</Badge>
              </div>
              <Form.Group className="mb-2">
                <Form.Label>Policy name</Form.Label>
                <Form.Control value={detail?.name || ''} readOnly plaintext />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>System prompt</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={8}
                  className={styles.mono}
                  value={systemPrompt}
                  onChange={(e) => setSystemPrompt(e.target.value)}
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Developer rules</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  className={styles.mono}
                  value={developerRules}
                  onChange={(e) => setDeveloperRules(e.target.value)}
                />
              </Form.Group>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Form.Label className="mb-0">Example pairs</Form.Label>
                <Button size="sm" variant="outline-primary" onClick={addExample}>
                  + Add example
                </Button>
              </div>
              {examples.map((ex, idx) => (
                <Card key={ex.id} className="mb-2">
                  <Card.Header className="py-1 small">Example {idx + 1}</Card.Header>
                  <Card.Body className="py-2">
                    <Form.Control
                      size="sm"
                      className="mb-1"
                      placeholder="Input utterance"
                      value={ex.input}
                      onChange={(e) => {
                        const next = [...examples];
                        next[idx] = { ...ex, input: e.target.value };
                        setExamples(next);
                      }}
                    />
                    <details>
                      <summary className="small">Expected output JSON</summary>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        className={`mt-1 ${styles.mono}`}
                        value={ex.outputJson}
                        onChange={(e) => {
                          const next = [...examples];
                          next[idx] = { ...ex, outputJson: e.target.value };
                          setExamples(next);
                        }}
                      />
                    </details>
                  </Card.Body>
                </Card>
              ))}
            </Card.Body>
          </Card>
        </Col>
        <Col lg={5}>
          <Card className="mb-3">
            <Card.Header>Test bench</Card.Header>
            <Card.Body>
              <Form.Group className="mb-2">
                <Form.Label>Sample input</Form.Label>
                <Form.Control as="textarea" rows={4} value={sampleInput} onChange={(e) => setSampleInput(e.target.value)} />
              </Form.Group>
              <Button className="mb-3" onClick={runTest}>
                Run test
              </Button>
              <div className="fw-semibold small mb-1">Output JSON</div>
              <pre className={styles.outputBlock}>{outputJson || 'Run a test to see output.'}</pre>
              <div className="mt-3">
                <div className="fw-semibold small mb-1">Validation results</div>
                <div className="small">{validation?.schemaOk ? '✓' : '✗'} JSON schema valid</div>
                <div className="small">{validation?.fieldsOk ? '✓' : '✗'} Required fields present</div>
                <div className="small">{validation?.confidenceOk ? '✓' : '✗'} Confidence &gt; threshold</div>
              </div>
              <div className="mt-3" style={{ maxWidth: 220 }}>
                <Chart options={radialOptions} series={radialSeries} type="radialBar" height={200} />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <div className={styles.stickyBar}>
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <Button variant="secondary" onClick={() => toast.success('Draft saved (simulated)')}>
            Save draft
          </Button>
          <Button variant="outline-primary" onClick={runTest}>
            Run test
          </Button>
          <Button disabled={!outputJson || !canPublish} onClick={async () => {
            try {
              await publishPromptAPI(id);
              toast.success('Published');
            } catch (e) {
              toast.error(e.message);
            }
          }}
          >
            Publish
          </Button>
          {!canPublish && outputJson && (
            <span className="small text-muted">Publish blocked until validation passes</span>
          )}
          <Button as={Link} variant="outline-secondary" to={platformUrl('ai-policies')}>
            Back
          </Button>
        </div>
      </div>
    </div>
  );
}
